import { MultiSelect, Button } from '@atrvd/saas-ui'
import { X } from 'lucide-react'
/**
 * Props for the Cash Position Summary filter bar (DCP-145).
 */
interface CashPositionFiltersProps {
  /** Selected pack ids, `NO_PACK` included. */
  packIds: string[]
  /** Facilities shown ticked. */
  facilityIds: string[]
  /** Called with the new pack selection. */
  onPackIdsChange: (packIds: string[]) => void
  /** Called with the facilities now ticked. */
  onFacilityIdsChange: (facilityIds: string[]) => void
  /** Called by the clear button. */
  onClear: () => void
  /** Pack options ({ value: id, label: name }), name-ASC, then "No Pack" if offered. */
  packOptions: Array<{ value: string; label: string }>
  /** Facility options ({ value: id, label: name }), name-ASC — the selected packs' only. */
  facilityOptions: Array<{ value: string; label: string }>
}

/**
 * Filter bar for the Cash Position Summary grid (DCP-145) — replaces the mockup's
 * per-pack/per-facility tabs with two multi-select pickers. Packs set the scope
 * and Facilities narrow it; empty on both means "All". The page owns what a
 * change means (see `selectCashPositionPacks`).
 *
 * DCP-306: rendered borderless and inline so it can live in the page-header's
 * top-right `actions` slot (compact, single row) instead of a separate boxed
 * filter bar — freeing vertical space so the report fits one screen.
 */
export function CashPositionFilters({
  packIds,
  facilityIds,
  onPackIdsChange,
  onFacilityIdsChange,
  onClear,
  packOptions,
  facilityOptions,
}: CashPositionFiltersProps) {
  // Any active filter enables the clear affordance.
  const hasActiveFilters = packIds.length > 0 || facilityIds.length > 0

  return (
    // Bottom-aligned so the two labelled selects and the clear button share a
    // baseline; no outer box/background — the page header provides the framing.
    <div className="flex flex-wrap items-end gap-3">
      {/* Pack Filter — the scope: the selected packs' facilities, "No Pack" for the rest. */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Packs</span>
        <MultiSelect
          options={packOptions}
          value={packIds}
          onChange={onPackIdsChange}
          placeholder="All"
          className="w-[200px]"
          searchable
          searchPlaceholder="Search packs..."
        />
      </div>

      {/* Facility Filter — narrows the scope; a picked pack's facilities come ticked. */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Facilities</span>
        <MultiSelect
          options={facilityOptions}
          value={facilityIds}
          onChange={onFacilityIdsChange}
          placeholder="All"
          className="w-[200px]"
          searchable
          searchPlaceholder="Search facilities..."
        />
      </div>

      {/* Clear Filters — only shown when something is selected. */}
      {hasActiveFilters && (
        <Button
          onClick={onClear}
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title="Clear filters"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
